import type { AsyncResourceVariant } from "../../index.js";
import {
  ListQueryModel,
  ListDataModel,
  AggregateMetaData,
  DataModel,
  ReferenceModel,
} from "../../base";
import { provideReact } from "../../react/index.js";
import { DateTime } from "luxon";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound";
import { config } from "../../config/config";
import type { LeadTechnologyData } from "../Lead/types";
import { getFormattedSalesVolume } from "../util/helper";
import type {
  UnlockedLeadData,
  UnlockedLeadListItemData,
  UnlockedLeadMetricsData,
  UnlockedLeadCompanyData,
  UnlockedLeadSocialMediaData,
  UnlockedLeadHosterInformationData,
  UnlockedLeadContactData,
  UnlockedLeadListQueryData,
} from "./types";

export class UnlockedLead extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "leadfinder",
    "unlockedlead",
  );

  private readonly customerId: string;

  public constructor(customerId: string, id: string) {
    super(id);
    this.customerId = customerId;
  }

  public static ofId(customerId: string, id: string) {
    return new UnlockedLead(customerId, id);
  }

  public static find = provideReact(async (customerId: string, id: string) => {
    const data = await config.behaviors.unlockedLead.find(customerId, id);
    if (data) {
      return new UnlockedLeadDetailed(customerId, data);
    }
  });

  public static get = provideReact(async (customerId: string, id: string) => {
    const unlockedLead = await this.find(customerId, id);
    assertObjectFound(unlockedLead, "unlockedLead", id);
    return unlockedLead;
  });

  public findDetailed = provideReact(
    () => UnlockedLead.find(this.customerId, this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<UnlockedLeadDetailed | undefined>>;

  public getDetailed = provideReact(
    () => UnlockedLead.get(this.customerId, this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<UnlockedLeadDetailed>>;

  public static query(
    customerId: string,
    query: UnlockedLeadListQueryData = {},
  ) {
    return new UnlockedLeadListQuery(customerId, query);
  }

  public async reserve() {
    await config.behaviors.unlockedLead.reserve(this.customerId, this.id);
  }

  public async removeReservation() {
    await config.behaviors.unlockedLead.removeReservation(
      this.customerId,
      this.id,
    );
  }
}

class UnlockedLeadCommon extends classes(
  DataModel<UnlockedLeadData | UnlockedLeadListItemData>,
  UnlockedLead,
) {
  public readonly domain: string;
  public readonly actualUrl: string;
  public readonly businessFields: string;
  public readonly description: string;
  public readonly screenshotBase64: string;
  public readonly potential: number;
  public readonly metrics: UnlockedLeadMetricsData;
  public readonly mainTechnology: LeadTechnologyData | undefined;
  public readonly company: UnlockedLeadCompanyData;
  public readonly technologies: LeadTechnologyData[];
  public readonly reservedAt: DateTime | undefined;
  public readonly isReserved: boolean;
  public readonly unlockedAt: DateTime;
  public readonly socialMedia: UnlockedLeadSocialMediaData[];
  public readonly hosterInformation: UnlockedLeadHosterInformationData;
  public readonly contact: UnlockedLeadContactData;
  public readonly languages: string[];
  public readonly reservationAllowed: boolean;

  public constructor(
    customerId: string,
    data: UnlockedLeadData | UnlockedLeadListItemData,
  ) {
    super([data], [customerId, data.leadId]);
    this.domain = data.domain;
    this.actualUrl = data.actualUrl;
    this.businessFields = data.businessFields.join(", ");
    this.description = data.description;
    this.screenshotBase64 = data.screenshot;
    this.potential = Math.round(data.potential * 100);
    this.metrics = data.metrics;
    this.mainTechnology = data.mainTechnology;
    this.company = data.company;
    this.technologies = data.technologies;
    this.reservedAt = data.reservedAt
      ? DateTime.fromISO(data.reservedAt)
      : undefined;
    this.isReserved = !!data.reservedAt;
    this.unlockedAt = DateTime.fromISO(data.unlockedAt);
    this.socialMedia = data.socialMedia;
    this.hosterInformation = data.hoster;
    this.contact = data.contact;
    this.languages = data.languages;
    this.reservationAllowed = data.reservationAllowed ?? false;
  }

  public get formattedSalesVolume(): string {
    return getFormattedSalesVolume(this.company.salesVolume);
  }

  public get potentialType(): "high" | "medium" | "low" {
    if (this.potential > 60) {
      return "high";
    } else if (this.potential > 50) {
      return "medium";
    } else {
      return "low";
    }
  }
}

export class UnlockedLeadDetailed extends classes(
  UnlockedLeadCommon,
  DataModel<UnlockedLeadData>,
) {
  public constructor(customerId: string, data: UnlockedLeadData) {
    super([customerId, data], [data]);
  }
}

export class UnlockedLeadListItem extends classes(
  UnlockedLeadCommon,
  DataModel<UnlockedLeadListItemData>,
) {
  public constructor(customerId: string, data: UnlockedLeadListItemData) {
    super([customerId, data], [data]);
  }
}

export class UnlockedLeadListQuery extends ListQueryModel<UnlockedLeadListQueryData> {
  private readonly customerId: string;

  public constructor(
    customerId: string,
    query: UnlockedLeadListQueryData = {},
  ) {
    super(query);
    this.customerId = customerId;
  }

  public refine(customerId: string, query: UnlockedLeadListQueryData) {
    return new UnlockedLeadListQuery(customerId, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.unlockedLead.list(
      this.customerId,
      this.query,
    );

    return new UnlockedLeadList(
      this.customerId,
      this.query,
      items.map((item) => new UnlockedLeadListItem(this.customerId, item)),
      totalCount,
    );
  }, [this.queryId, "unlockedLead"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine(this.customerId, {
      limit: 1,
    }).execute();
    return totalCount;
  }, [this.queryId, "unlockedLead"]);
}

export class UnlockedLeadList extends classes(
  UnlockedLeadListQuery,
  ListDataModel<UnlockedLeadListItem>,
) {
  public constructor(
    customerId: string,
    query: UnlockedLeadListQueryData,
    leads: UnlockedLeadListItem[],
    totalCount: number,
  ) {
    super([customerId, query], [leads, totalCount]);
  }

  public static reserved(
    customerId: string,
    query: UnlockedLeadListQueryData = {},
  ) {
    return new UnlockedLeadListQuery(customerId, {
      ...query,
      reserved: true,
    });
  }
}
